/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core'
import { NumericFormat } from 'react-number-format'
import { Cancel, NoteAdd } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import { Item } from '../../../schemas/Item'
import ItemService from '../../../services/Item'
import permissions from '../../../permissions.json'
import { redirect, useLocation } from 'react-router-dom'
import Utils from '../../../utils'
import { urls } from '../../../urls'
import PageContainer from '../Common/PageContainer'
import FormActions from '../Common/FormActions'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Crear productos'
  },
  body: {
    field: {
      code: {
        name: 'code',
        helperText: 'Escriba el código del producto',
        placeholder: 'Código'
      },
      account: {
        name: 'account',
        helperText: 'Cuenta contable del producto',
        placeholder: 'Cuenta',
        validationError: 'Debe ser de 10 dígitos'
      },
      description: {
        name: 'description',
        helperText: 'Escriba la descripción del producto',
        placeholder: 'Nombre de producto'
      },
      price: {
        name: 'price',
        helperText: 'Escriba el precio en pesos colombianos',
        placeholder: 'Precio'
      },
      unitMeasure: {
        name: 'unitMeasure',
        helperText: 'Escoja una unidad de medida',
        options: [
          { description: 'Unidad', code: '70' },
          { description: 'Kilogramo', code: '40' },
          { description: 'Galón', code: '686' },
          { description: 'Metro cúbico', code: '594' },
          { description: 'Metro cuadrado', code: '472' },
        ]
      },
      itemType: {
        name: 'itemType',
        helperText: 'Tipo de producto',
        options: [
          { description: 'UNSPSC', code: '1' },
          { description: 'GTIN', code: '2' },
          { description: 'Partida Arancelarias', code: '3' },
          { description: 'Estándar de adopción del contribuyente', code: '4' }
        ]
      }
    }
  }
}

// ------------ Init state -----------
interface State {
  form: Item
  validations: {
    errorMinLengthAccount: boolean
  }
  isEdit: boolean
  loading: boolean
}

const initState: State = {
  form: {
    code: '',
    account: 0,
    description: '',
    price: undefined,
    unitMeasure: undefined,
    itemType: undefined
  },
  validations: {
    errorMinLengthAccount: false
  },
  isEdit: false,
  loading: false
}

export default function ItemForm() {
  const classes = useStyles()
  const { search } = useLocation()

  const [state, setState] = useState<State>(initState)
  const { userContext } = React.useContext(
    UserContext
  ) as UserContextType

  React.useEffect(() => {
    async function loadData() {
      const account = Utils.getIdFromUrl(search)

      if (account) {
        setState({
          ...state,
          loading: true
        })
        const itemService = new ItemService()
        const item = await itemService.getItemByCode(account)
        if (item) {
          setState({
            ...state,
            form: item,
            loading: false,
            isEdit: true
          })
        }
      }
    }
    loadData()
  }, [])

  // HandleChange is the handler to update the state
  // of the state, while the user change the options 
  // on an input select.
  const handleChangeSelect = (name: string, item: { code: string, description: string }) => {
    setState({
      ...state,
      form:{
        ...state.form,
        [name]: item
      }
    })
  }

  // HandleChange is the handler to update the state
  // of the state, while the user writes on an input.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    let errorMinLengthAccount = state.validations.errorMinLengthAccount

    if (name === 'account') {
      errorMinLengthAccount = value.length < Number(process.env.REACT_APP_MAX_LENGTH_CONTABLE_ACCOUNT)
    }
    setState({
      ...state,
      form: {
        ...state.form,
        [name]: value
      },
      validations: {
        errorMinLengthAccount
      }
    })
  }

  // HandleSubmit is the handler to verify and send
  // the state to redux (redux send the info to backend
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      // validations
      if (state.validations.errorMinLengthAccount && userContext.permissions.some(p => p === permissions.super_admin)) {
        throw new Error(`La cuenta debe tener ${process.env.REACT_APP_MAX_LENGTH_CONTABLE_ACCOUNT} dígitos`)
      }

      const itemService = new ItemService()
      let itemCreated

      if (state.isEdit) {
        itemCreated = await itemService.updateItem(state.form)
      } else {
        itemCreated = await itemService.saveItem(state.form)
      }

      setState(initState)
      redirect(urls.app.main.item.form)
      return toast.success(`El producto ${itemCreated.code} fue ${state.isEdit ? 'actualizado' : 'creado'} con éxito`)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      } else if (error instanceof Error) {
        return toast.error(error.message)
      }
    }

  }

  const cancel = () => {
    setState(initState)
  }

  return (
    <PageContainer title={texts.header.description}>

      {/* ----- Body ------*/}
      <form onSubmit={(e) => handleSubmit(e)}>
        <Grid container spacing={3}>

          {/* ----- Form: code ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <TextField
              required={true}
              name={texts.body.field.code.name}
              label={texts.body.field.code.placeholder}
              value={state.form.code}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.code.helperText}
            />
          </Grid>

          {/* ----- Form: account, only the admin can create this field ------*/}
          {
            userContext.permissions.find(permission => permission === permissions.super_admin) ?
            (
              <Grid item md={6} sm={6} xs={12}>
                <TextField
                  required={true}
                  name={texts.body.field.account.name}
                  label={texts.body.field.account.placeholder}
                  value={state.form.account}
                  variant='outlined'
                  fullWidth
                  onChange={handleChange}
                  helperText={ state.validations.errorMinLengthAccount ? texts.body.field.account.validationError : texts.body.field.account.helperText}
                  error={state.validations.errorMinLengthAccount}
                  inputProps={{ maxLength: process.env.REACT_APP_MAX_LENGTH_CONTABLE_ACCOUNT, inputMode: 'numeric' }}
                />
              </Grid>
            ) : ''
          }

          {/* ----- Form: description ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <TextField
              required={true}
              name={texts.body.field.description.name}
              label={texts.body.field.description.placeholder}
              value={state.form.description}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.description.helperText}
            />
          </Grid>

          {/* ----- Form: price ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <NumericFormat
              name={texts.body.field.price.name}
              value={state.form.price ?? ''}
              onValueChange={(values) => {
                const rawPrice = values.floatValue
                let price: number | undefined = undefined
                if (rawPrice !== undefined) {
                  price = Utils.roundToMaxDecimals(rawPrice)
                }
                setState({
                  ...state,
                  form: { ...state.form, price: price }
                })
              }}
              decimalScale={Utils.getMaxPriceDecimals()}
              thousandSeparator="."
              decimalSeparator=","
              prefix="COP "
              allowNegative={false}
              customInput={TextField}
              label={texts.body.field.price.placeholder}
              variant="outlined"
              fullWidth
              inputMode="decimal"
              helperText={texts.body.field.price.helperText}
            />
          </Grid>

          {/* ----- Form: unit measure ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="unit-measure-label">Unidad de medida</InputLabel>
              <Select
                labelId="unit-measure-label"
                label="Unidad de medida"
                name={texts.body.field.unitMeasure.name}
                value={state.form.unitMeasure?.code ?? ''}
              >
                {
                  texts.body.field.unitMeasure.options.map((item) =>
                    <MenuItem
                      key={item.description}
                      value={item.code}
                      onClick={() => handleChangeSelect(texts.body.field.unitMeasure.name, item)}
                    >
                      {item.description}
                    </MenuItem>
                  )
                }
              </Select>
              <FormHelperText>{texts.body.field.unitMeasure.helperText}</FormHelperText>
            </FormControl>
          </Grid>

          {/* ----- Form: item type ------*/}
          <Grid item md={6} sm={6} xs={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="item-type-label">Tipo de producto</InputLabel>
              <Select
                labelId="item-type-label"
                label="Tipo de producto"
                name={texts.body.field.itemType.name}
                value={state.form.itemType?.code ?? ''}
              >
                {
                  texts.body.field.itemType.options.map((item) =>
                    <MenuItem
                      key={item.description}
                      value={item.code}
                      onClick={() => handleChangeSelect(texts.body.field.itemType.name, item)}
                    >
                      {item.description}
                    </MenuItem>
                  )
                }
              </Select>
              <FormHelperText>{texts.body.field.itemType.helperText}</FormHelperText>
            </FormControl>
          </Grid>

        </Grid>

        <FormActions>
          <Button type="button" variant="outlined" color="secondary" startIcon={<Cancel />} onClick={cancel}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" startIcon={<NoteAdd />}>
            {state.isEdit ? 'Actualizar' : 'Guardar'}
          </Button>
        </FormActions>
      </form>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
